import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Table, Form, Row, Col, Pagination, InputGroup, Card } from 'react-bootstrap';

import { formatDateOnly } from '../../../utils/dateUtils';
const InformationPagesListing = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters and Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchPages();
    }, []);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedStatus]);

    const fetchPages = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/web-pages/information');
            setPages(response.data.data);
            setLoading(false);
        } catch (error) {
            toast.error("Failed to fetch information pages");
            setLoading(false);
        }
    };

    const handleStatusToggle = async (id) => {
        try {
            const response = await superAdminApiClient.post('/superadmin/web-pages/information/status', { id });
            if (response.data.success) {
                toast.success(response.data.message);
                setPages(pages.map(page => 
                    page.id === id ? { ...page, status: page.status === 'active' ? 'inactive' : 'active' } : page
                ));
            }
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Web Page?',
            text: "Are you sure you want to delete this page? This will remove it from the footer as well. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await superAdminApiClient.delete(`/superadmin/web-pages/information/${id}`);
                    if (response.data.success) {
                        Swal.fire('Deleted!', 'Information page has been successfully deleted.', 'success');
                        setPages(pages.filter(page => page.id !== id));
                    }
                } catch (error) {
                    toast.error("Failed to delete information page");
                }
            }
        });
    };

    // Filter Logic
    const filteredPages = pages.filter(page => {
        const title = page.page_title || '';
        const status = page.status || 'active';

        const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (page.slug || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = selectedStatus === 'all' || status === selectedStatus;

        return matchesSearch && matchesStatus;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredPages.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredPages.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="container-fluid">
            {/* Header section */}
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between m-0 mb-3">
                        <h4 className="mb-sm-0 font-weight-bold text-dark">Information Web Pages</h4>
                        <div className="page-title-right">
                            <Link to="/superadmin/web-pages/information/add" className="btn btn-primary d-flex align-items-center gap-1">
                                <i className="ri-add-line fs-16"></i> Create Page
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters section */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                            <Row className="g-3">
                                <Col md={8} lg={9}>
                                    <Form.Group className="mb-0">
                                        <InputGroup>
                                            <InputGroup.Text className="bg-light border-0 text-muted">
                                                <i className="ri-search-line"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by page title or slug..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="bg-light border-0"
                                                style={{ boxShadow: 'none' }}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={4} lg={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Select
                                            value={selectedStatus}
                                            onChange={(e) => setSelectedStatus(e.target.value)}
                                            className="bg-light border-0"
                                            style={{ boxShadow: 'none' }}
                                        >
                                            <option value="all">All Statuses</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* List section */}
            <div className="row">
                <div className="col-xl-12">
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table className="table align-middle table-nowrap mb-0 table-hover">
                                    <thead className="table-light text-muted">
                                        <tr>
                                            <th className="py-3 px-4" style={{ width: '80px' }}>Image</th>
                                            <th>Page Title</th>
                                            <th>Slug</th>
                                            <th>Meta Title</th>
                                            <th>Status</th>
                                            <th>Last Updated</th>
                                            <th className="text-center px-4" style={{ width: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-dark">
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : currentItems.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5 text-muted">
                                                    <i className="ri-pages-line fs-36 d-block mb-2 text-secondary"></i>
                                                    No web pages found matching the filters.
                                                </td>
                                            </tr>
                                        ) : currentItems.map((page) => (
                                            <tr key={page.id}>
                                                <td className="py-3 px-4">
                                                    {page.image_url ? (
                                                        <img 
                                                            src={page.image_url} 
                                                            alt={page.page_title} 
                                                            className="rounded" 
                                                            style={{ width: '55px', height: '38px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: '55px', height: '38px', border: '1px solid #e3e3e3' }}>
                                                            <i className="ri-image-line text-muted"></i>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="font-weight-medium text-dark">
                                                    {page.page_title}
                                                </td>
                                                <td>
                                                    <code className="text-primary">{page.slug}</code>
                                                </td>
                                                <td className="text-muted" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {page.meta_title || '-'}
                                                </td>
                                                <td>
                                                    <div className="form-check form-switch form-switch-md">
                                                        <Form.Check 
                                                            type="switch"
                                                            id={`status-switch-${page.id}`}
                                                            checked={page.status === 'active'}
                                                            onChange={() => handleStatusToggle(page.id)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="text-secondary fs-13">
                                                    {formatDateOnly(page.updated_at || page.updatedAt)}
                                                </td>
                                                <td className="px-4">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <Link to={`/superadmin/web-pages/information/edit/${page.id}`} className="btn btn-sm btn-light border" style={{ padding: '0.35rem 0.55rem' }}>
                                                            <i className="ri-pencil-fill text-muted fs-14"></i>
                                                        </Link>
                                                        <button 
                                                            onClick={() => handleDelete(page.id)} 
                                                            className="btn btn-sm btn-light border text-danger" 
                                                            style={{ padding: '0.35rem 0.55rem' }}
                                                        >
                                                            <i className="ri-delete-bin-fill fs-14"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted fs-13">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPages.length)} of {filteredPages.length} entries
                                    </div>
                                    <Pagination className="mb-0 shadow-none">
                                        <Pagination.Prev 
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {[...Array(totalPages)].map((_, idx) => (
                                            <Pagination.Item 
                                                key={idx + 1}
                                                active={currentPage === idx + 1}
                                                onClick={() => handlePageChange(idx + 1)}
                                            >
                                                {idx + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next 
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default InformationPagesListing;
